import React, { useContext, useEffect, useState } from 'react';
// @ts-ignore
import { closeSpinner, openSpinner } from '@paytheory/pay-theory-ui';
import { AppContext } from '../../App';
import * as network from '../../network';
import { UserPool } from '../../network/API/types';
import {
  BETA_PORTAL_LEARN_MORE_URL,
  betaPortalBannerMessage,
  betaPortalCreateErrorMessage,
  betaPortalDismissCta,
  betaPortalLearnMoreCta,
  betaPortalLoginCta,
  betaPortalMissingProfileMessage,
  betaPortalPermissions,
  buildMerchantPortalMigrationDismissal,
  getEmailSentMessage,
  getNewPortalUrl,
  isUserAlreadyExistsError,
  resetAllMerchantPortalMigrationDismissals,
  setMerchantPortalMigrationDismissal,
  shouldShowMerchantPortalMigrationPrompt,
} from './model';

const MerchantPortalMigrationPrompt = () => {
  const { merchant, merchantUID, userProfile, ErrorMessage, SuccessMessage } =
    useContext(AppContext);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const email = userProfile.email?.trim();
  const firstName = userProfile.firstName?.trim();
  const lastName = userProfile.lastName?.trim();

  const persistPromptSuppression = () => {
    const dismissal = buildMerchantPortalMigrationDismissal(email, merchantUID);
    if (dismissal) {
      setMerchantPortalMigrationDismissal(dismissal);
    }
  };

  const hidePrompt = () => {
    setShowPrompt(false);
  };

  const dismissPrompt = () => {
    persistPromptSuppression();
    hidePrompt();
  };

  useEffect(() => {
    (window as any).__resetPortalMigrationPrompt = () => {
      resetAllMerchantPortalMigrationDismissals();
      restorePrompt();
      console.log(
        '[MerchantPortalMigrationPrompt] Dismissal cleared — banner will reappear.',
      );
    };
    return () => {
      delete (window as any).__resetPortalMigrationPrompt;
    };
  }, []);

  const restorePrompt = () => {
    setShowPrompt(
      shouldShowMerchantPortalMigrationPrompt({
        email: userProfile.email,
        merchantUID,
      }),
    );
  };

  useEffect(() => {
    if (!merchant) {
      setShowPrompt(false);
      return;
    }
    setShowPrompt(
      shouldShowMerchantPortalMigrationPrompt({ email, merchantUID }),
    );
  }, [email, merchant, merchantUID]);

  const handleLoginClick = async () => {
    if (!email || !merchantUID || !firstName || !lastName) {
      ErrorMessage(betaPortalMissingProfileMessage);
      return;
    }

    setIsCreating(true);
    openSpinner();

    try {
      const response = await network.users.createV2({
        input: {
          email,
          first_name: firstName,
          last_name: lastName,
          merchant_uid: merchantUID,
          permissions: JSON.stringify(betaPortalPermissions),
          user_pool: UserPool.MERCHANT,
        },
      });

      if (response.errors || !response.data) {
        const errorMessage = response.errors?.[0] ?? '';
        if (isUserAlreadyExistsError(errorMessage)) {
          persistPromptSuppression();
          hidePrompt();
          window.open(getNewPortalUrl(), '_blank', 'noopener,noreferrer');
        } else {
          ErrorMessage(betaPortalCreateErrorMessage);
        }
        return;
      }

      persistPromptSuppression();
      hidePrompt();
      SuccessMessage(getEmailSentMessage(email));
    } finally {
      closeSpinner();
      setIsCreating(false);
    }
  };

  if (!merchant) {
    return null;
  }

  return (
    <>
      {showPrompt ? (
        <div className="merchant-portal-migration-lane">
          <div className="merchant-portal-migration-prompt">
            <p>
              {betaPortalBannerMessage}{' '}
              <a
                className="merchant-portal-migration-learn-more"
                href={BETA_PORTAL_LEARN_MORE_URL}
                target="_blank"
                rel="noopener noreferrer">
                {betaPortalLearnMoreCta}
              </a>
            </p>
            <div className="merchant-portal-migration-actions">
              <button
                className="merchant-portal-migration-button merchant-portal-migration-button-primary"
                data-testid="merchant-portal-migration-login"
                disabled={isCreating}
                onClick={handleLoginClick}
                type="button">
                {betaPortalLoginCta}
              </button>
              <button
                className="merchant-portal-migration-button merchant-portal-migration-button-text"
                data-testid="merchant-portal-migration-dismiss"
                disabled={isCreating}
                onClick={dismissPrompt}
                type="button">
                {betaPortalDismissCta}
              </button>
            </div>
          </div>
          {/*@ts-ignore*/}
          <style jsx="true">
            {`
              .merchant-portal-migration-lane {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                height: 0;
                z-index: 50;
                pointer-events: none;
              }

              .merchant-portal-migration-prompt {
                position: absolute;
                top: 12px;
                left: clamp(16px, 22vw, 320px);
                right: clamp(16px, 22vw, 320px);
                box-sizing: border-box;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 16px;
                height: 40px;
                padding: 0 8px 0 16px;
                border: 1px solid var(--pt-purple);
                border-radius: 8px;
                background: var(--purple-light);
                box-shadow: 1px 1px 1px var(--black-opaque-8);
                overflow: hidden;
                pointer-events: auto;
              }

              .merchant-portal-migration-prompt p {
                margin: 0;
                margin-right: 24px;
                color: var(--black);
                font-size: 15px;
                line-height: 20px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
              }

              .merchant-portal-migration-learn-more {
                color: var(--pt-purple);
                text-decoration: underline;
                white-space: nowrap;
              }

              .merchant-portal-migration-learn-more:hover {
                opacity: 0.75;
              }

              .merchant-portal-migration-actions {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-shrink: 0;
              }

              .merchant-portal-migration-button {
                border: 0;
                background: transparent;
                font-family: var(--secondary-font);
                text-transform: uppercase;
                cursor: pointer;
                transition:
                  background 0.15s ease-in-out,
                  color 0.15s ease-in-out,
                  opacity 0.15s ease-in-out;
              }

              .merchant-portal-migration-button:disabled {
                cursor: default;
                opacity: 0.64;
              }

              .merchant-portal-migration-button-primary {
                box-sizing: border-box;
                height: 30px;
                min-height: 30px;
                padding: 0 12px;
                border-radius: 10px;
                color: var(--white);
                background: var(--pt-purple);
                font-size: 13px;
                line-height: 13px;
                letter-spacing: 0.04em;
                white-space: nowrap;
              }

              .merchant-portal-migration-button-primary:not(:disabled):hover {
                background: var(--pt-purple-opaque-56);
              }

              .merchant-portal-migration-button-primary:focus-visible,
              .merchant-portal-migration-button-text:focus-visible {
                outline: 2px solid var(--pt-purple);
                outline-offset: 2px;
              }

              .merchant-portal-migration-button-text {
                padding: 0;
                font-size: 14px;
                line-height: 18px;
                color: var(--pt-purple);
                white-space: nowrap;
              }

              .merchant-portal-migration-button-text:not(:disabled):hover {
                text-decoration: underline;
              }

              @media only screen and (max-width: 760px) {
                .merchant-portal-migration-prompt {
                  left: 16px;
                  right: 16px;
                  height: auto;
                  min-height: 40px;
                  padding: 10px 12px;
                  align-items: flex-start;
                  flex-direction: column;
                }

                .merchant-portal-migration-prompt p {
                  margin-right: 0;
                  white-space: normal;
                }

                .merchant-portal-migration-actions {
                  width: 100%;
                  justify-content: flex-end;
                }
              }

              @media only screen and (max-width: 600px) {
                .merchant-portal-migration-actions {
                  flex-direction: column;
                  align-items: stretch;
                }
              }
            `}
          </style>
        </div>
      ) : null}
    </>
  );
};

export default MerchantPortalMigrationPrompt;
